import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatErr } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome back, ${u.full_name?.split(" ")[0] || "investor"}`);
      nav(u.role === "admin" ? "/admin-duo-secure" : "/dashboard");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16" data-testid="login-page">
      <div className="grid lg:grid-cols-2 gap-10 max-w-5xl w-full">
        <div className="hidden lg:flex flex-col justify-center">
          <Link to="/" className="font-display text-3xl font-black mb-6">Duo<span className="gold-text">capital</span></Link>
          <h1 className="font-display text-4xl font-black leading-tight">Welcome back, <span className="gold-grad">investor</span>.</h1>
          <p className="text-white/60 mt-4 max-w-md">Log in to access your portfolio, monitor the AI trading engine, and manage your wealth.</p>
          <div className="mt-8 flex items-center gap-3 text-sm text-[#00D68F]">
            <ShieldCheck className="w-5 h-5" /> Protected by bank-grade encryption
          </div>
        </div>
        <form onSubmit={submit} className="glass-strong rounded-2xl p-8 space-y-5" data-testid="login-form">
          <div className="lg:hidden">
            <Link to="/" className="font-display text-3xl font-black mb-2 block">Duo<span className="gold-text">capital</span></Link>
          </div>
          <div>
            <div className="label-eyebrow">Sign in</div>
            <h2 className="font-display text-3xl font-bold mt-2">Access your dashboard</h2>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/60 block mb-2">Email</label>
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 rounded-lg" placeholder="you@duocapital.com" data-testid="login-email" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/60 block mb-2">Password</label>
            <div className="relative">
              <input type={showPassword ? "text" : "password"} required value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-3 rounded-lg pr-12" placeholder="••••••••" data-testid="login-password" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="text-right mt-2">
              <Link to="/forgot-password" className="text-xs text-[#D4AF37] hover:text-[#FCEB9E]" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-50" data-testid="login-submit">{loading ? "Signing in..." : "Sign In"}</button>
          <div className="text-sm text-white/60 text-center">
            Don't have an account? <Link to="/register" className="text-[#D4AF37]" data-testid="login-to-register">Create one</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
